import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import {
  UserPlus,
  CircleNotch,
  PaperPlaneTilt,
  MagnifyingGlass,
  Trash,
} from "@phosphor-icons/react";
import type { Supervisor, SearchUser } from "@/types";

export interface SupervisorsSectionProps {
  companyId: string;
  supervisors: Supervisor[];
  onRemove: (id: string) => void;
  isRemoving: boolean;
}

export function SupervisorsSection({
  companyId,
  supervisors,
  onRemove,
  isRemoving,
}: SupervisorsSectionProps) {
  const queryClient = useQueryClient();
  const [addMode, setAddMode] = useState<null | "search" | "invite">(null);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: searchResults = [] } = useQuery({
    queryKey: ["users", "search", searchQuery],
    queryFn: async () => {
      const res = await fetch(
        `/api/users/search?q=${encodeURIComponent(searchQuery)}`
      );
      await throwIfNotOk(res, "Greška pri pretrazi");
      return res.json() as Promise<SearchUser[]>;
    },
    enabled: searchQuery.length >= 2,
  });

  const addMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await fetch(`/api/companies/${companyId}/supervisors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
      await throwIfNotOk(res, "Greška pri dodavanju supervizora");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "companies", companyId, "supervisors"],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "company", companyId],
      });
      toast.success("Supervizor dodan");
      setAddMode(null);
      setSearchQuery("");
    },
  });

  const [inviteEmail, setInviteEmail] = useState("");
  const inviteMutation = useMutation({
    mutationFn: async (email: string) => {
      const res = await fetch(
        `/api/companies/${companyId}/supervisors/invite`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );
      await throwIfNotOk(res, "Greška pri slanju pozivnice");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "companies", companyId, "supervisors"],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "company", companyId],
      });
      toast.success("Pozivnica poslana");
      setAddMode(null);
      setInviteEmail("");
    },
  });

  return (
    <div className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm">
      <div className="flex items-center justify-between border-b border-border/40 px-4 py-3">
        <h2 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          Supervizori
        </h2>
        {!addMode && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setAddMode("search")}
          >
            <UserPlus className="size-3.5" />
            Dodaj
          </Button>
        )}
      </div>

      {addMode && (
        <div className="border-b border-border/40 px-4 py-3 space-y-2">
          <div className="flex gap-1.5">
            <Button
              size="sm"
              variant={addMode === "search" ? "default" : "outline"}
              onClick={() => setAddMode("search")}
              className="text-xs"
            >
              <MagnifyingGlass className="size-3" />
              Pretraži
            </Button>
            <Button
              size="sm"
              variant={addMode === "invite" ? "default" : "outline"}
              onClick={() => setAddMode("invite")}
              className="text-xs"
            >
              <PaperPlaneTilt className="size-3" />
              Pozovi
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setAddMode(null);
                setSearchQuery("");
                setInviteEmail("");
              }}
              className="ml-auto text-xs"
            >
              Otkaži
            </Button>
          </div>

          {addMode === "search" && (
            <div className="space-y-2">
              <Input
                placeholder="Pretraži korisnike..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="text-sm"
              />
              {searchResults.length > 0 && (
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {searchResults.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between rounded-lg bg-muted/30 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">
                          {user.name}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {user.email}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px] shrink-0"
                        onClick={() => addMutation.mutate(user.id)}
                        disabled={addMutation.isPending}
                      >
                        Dodaj
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {addMode === "invite" && (
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="email@primjer.ba"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="text-sm"
              />
              <Button
                size="sm"
                onClick={() => inviteMutation.mutate(inviteEmail)}
                disabled={!inviteEmail || inviteMutation.isPending}
              >
                {inviteMutation.isPending ? (
                  <CircleNotch className="size-3.5 animate-spin" />
                ) : (
                  <PaperPlaneTilt className="size-3.5" />
                )}
                Pošalji
              </Button>
            </div>
          )}
        </div>
      )}

      <div>
        {supervisors.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            Nema supervizora.
          </p>
        ) : (
          supervisors.map((s, i) => (
            <div
              key={s.id}
              className={cn(
                "flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-primary/[0.03]",
                i !== supervisors.length - 1 && "border-b border-border/40"
              )}
            >
              <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary-foreground">
                {s.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">
                  {s.name ?? "Pozivnica poslana"}
                </div>
                <div className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-[11px] text-muted-foreground">
                    {s.email}
                  </span>
                  {s.inviteStatus === "pending" && (
                    <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                      Čeka
                    </span>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                className="shrink-0 text-muted-foreground hover:text-destructive"
                disabled={isRemoving}
                onClick={() => onRemove(s.id)}
              >
                <Trash className="size-3.5" />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
